import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";

export type CustomerUser = {
  id: string;
  email: string;
  fullName?: string | null;
  name?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  role?: "ADMIN" | "CUSTOMER" | "SUPPORT" | null;
  // Sprint 5 Slice 2 — flag de verificación de email del backend.
  // ISO string (Date serialized) cuando verificado; null mientras pendiente.
  emailVerifiedAt?: string | null;
};

export type StorefrontOrganization = {
  id: string;
  name: string;
  slug: string;
};

type AuthState = {
  hydrated: boolean;
  accessToken: string | null;
  // Lane 2.5.A — token CSRF efímero emitido por el API en register/login/
  // refresh y en GET /auth/csrf. NO se persiste en localStorage: el secret
  // vive en una cookie httpOnly, este token es el par público que viaja en
  // `x-csrf-token`. En hydration, si hay accessToken se re-fetchea via
  // /auth/csrf.
  csrfToken: string | null;
  user: CustomerUser | null;
  organization: StorefrontOrganization | null;

  setToken: (t: string | null) => void;
  setCsrfToken: (t: string | null) => void;
  setUser: (u: CustomerUser | null) => void;
  setOrganization: (o: StorefrontOrganization | null) => void;

  setSession: (payload: {
    accessToken: string | null;
    csrfToken?: string | null;
    customer?: CustomerUser | null;
    user?: CustomerUser | null;
    organization?: StorefrontOrganization | null;
  }) => void;

  logoutLocal: () => void;
  markHydrated: () => void;
};

const storage: StateStorage = {
  getItem: (name) => {
    const v = localStorage.getItem(name);
    return v ?? null;
  },
  setItem: (name, value) => {
    localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      hydrated: false,
      accessToken: null,
      csrfToken: null,
      user: null,
      organization: null,

      setToken: (t) => set({ accessToken: t }),
      setCsrfToken: (t) => set({ csrfToken: t }),
      setUser: (u) => set({ user: u }),
      setOrganization: (o) => set({ organization: o }),

      setSession: ({
        accessToken,
        csrfToken,
        customer,
        user,
        organization,
      }) =>
        set((prev) => ({
          accessToken,
          csrfToken:
            csrfToken !== undefined ? csrfToken : prev.csrfToken,
          user: customer ?? user ?? null,
          organization: organization ?? null,
        })),

      logoutLocal: () => {
        set({
          accessToken: null,
          csrfToken: null,
          user: null,
          organization: null,
        });
      },

      markHydrated: () => set({ hydrated: true }),
    }),
    {
      name: "kwad.storefront.auth",
      storage: createJSONStorage(() => storage),
      // Sprint 5 Slice 2: bump V2 → V3 al agregar emailVerifiedAt al user.
      // Migrate: drop user/accessToken viejos para que el bootstrap forzado
      // al próximo arranque haga un /me fresco con el campo nuevo.
      version: 3,
      // csrfToken NO se persiste: es efímero y se re-fetchea en hydration via
      // GET /auth/csrf cuando hay accessToken.
      partialize: (s) => ({
        accessToken: s.accessToken,
        user: s.user,
        organization: s.organization,
      }),
      migrate: (persisted, version) => {
        if (version < 3) {
          return {
            accessToken: null,
            user: null,
            organization: null,
          };
        }
        return persisted as Partial<AuthState>;
      },
      onRehydrateStorage: () => (state) => {
        state?.markHydrated();
      },
    },
  ),
);

export const authSelectors = {
  token: () => useAuthStore.getState().accessToken,
  csrfToken: () => useAuthStore.getState().csrfToken,
  user: () => useAuthStore.getState().user,
  organization: () => useAuthStore.getState().organization,
  isAuthed: () => !!useAuthStore.getState().accessToken,
};

export function resetAuthPersist() {
  try {
    localStorage.removeItem("auth"); // legado viejo
    localStorage.removeItem("kwad.storefront.auth");
  } catch {
    // no-op
  }

  useAuthStore.persist?.clearStorage?.();
  useAuthStore.setState({
    accessToken: null,
    csrfToken: null,
    user: null,
    organization: null,
    hydrated: true,
  });
}
